// U4 "No draft? Say why, and ask one question." When Claude can't tell what the owner would say, the
// card shows one line on why and one question with short answers ("Yes · No · Tell me"). Each
// answer asks Claude for the draft that follows from it; "Tell me" opens the instruction input for
// this card. Calm: a plain box, no warning colours.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { CardBlock } from "@shared/core";
import { TELL_ME } from "@shared/drafts";
import { useCard } from "@web/lib/feedQueries";
import { dock } from "@web/shell/dockStore";
import { Button } from "@web/ui/Button";
import { askForDraft } from "./draftAhead";

export function DraftQuestion({
  block,
  cardId,
}: {
  block: CardBlock;
  cardId?: string | undefined;
}) {
  const queryClient = useQueryClient();
  const card = useCard(cardId ?? null).data;
  const [picked, setPicked] = useState<string | null>(null);
  const work = card?.drafting;
  const writing = work?.byOwner && (work.status === "queued" || work.status === "working");
  const done = !!card && !card.status.startsWith("to_review");
  const answer = async (choice: string) => {
    if (!cardId) return;
    setPicked(choice);
    const ok = await askForDraft(queryClient, cardId, choice);
    if (!ok) setPicked(null);
  };
  const tellMe = () => {
    dock.setScope("card");
    dock.focus();
  };
  return (
    <section
      data-testid="draft-question"
      className="flex flex-col gap-2 rounded-lg border border-line bg-bg px-4 py-3"
    >
      {block.text ? <p className="text-sm text-muted">{block.text}</p> : null}
      <p className="font-medium text-ink">{block.question}</p>
      {writing ? (
        <p className="text-sm text-muted" aria-live="polite">
          Writing the draft{picked ? ` for “${picked}”` : ""}…
        </p>
      ) : (
        <div role="group" aria-label={block.question} className="flex flex-wrap gap-2">
          {(block.choices ?? []).map((choice) => (
            <Button
              key={choice}
              size="sm"
              variant="secondary"
              disabled={done || picked !== null}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => void answer(choice)}
            >
              {choice}
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            disabled={done}
            onPointerDown={(e) => e.preventDefault()}
            onClick={tellMe}
          >
            {TELL_ME}
          </Button>
        </div>
      )}
    </section>
  );
}
